import { useEffect, useState } from 'react';
import type { CreateNodeDraft } from '../../../core/entities/ai-assist-config';
import { assembleDeidentificationRules } from '../../../core/policies/anonymization/assemble-deidentification-rules';
import { useGraphStore } from '../../graph-view/services/graph-store';
import { useLocale } from '../i18n/LocaleProvider';
import { useDeidentificationSettingsStore } from '../services/deidentification-settings-store';
import { DeidentificationCandidateList } from './DeidentificationCandidateList';
import { ManualRuleEditor } from './ManualRuleEditor';
import { PreviewTextPanel } from './PreviewTextPanel';

export function DeidentificationSettingsPanel() {
  const readonlyMode = useGraphStore((state) => state.readonlyMode);
  const { t } = useLocale();
  const {
    config,
    previewSample,
    warnings,
    humanOnly,
    setEnabled,
    setHumanOnly,
    setCandidateEnabled,
    setCandidateReplacement,
    addManualRule,
    updateManualRule,
    removeManualRule,
    setPreviewInput,
    runPreview,
    setCreateNodePromptTemplate,
    setCreateNodeExamplePayload,
  } = useDeidentificationSettingsStore();
  const assembly = assembleDeidentificationRules(config.deIdentification);
  const [createNodeExampleJson, setCreateNodeExampleJson] = useState(() => JSON.stringify(config.createNodePromptContract.examplePayload, null, 2));
  const [createNodeExampleIssue, setCreateNodeExampleIssue] = useState<string | null>(null);

  useEffect(() => {
    setCreateNodeExampleJson(JSON.stringify(config.createNodePromptContract.examplePayload, null, 2));
    setCreateNodeExampleIssue(null);
  }, [config.createNodePromptContract.examplePayload]);

  function commitCreateNodeExampleJson() {
    try {
      const parsed = JSON.parse(createNodeExampleJson) as CreateNodeDraft;
      setCreateNodeExamplePayload(parsed);
      setCreateNodeExampleIssue(null);
    } catch {
      setCreateNodeExampleIssue(t('settings.deidentification.createNodeExampleInvalid'));
    }
  }

  return (
    <section aria-label={t('settings.deidentification.title')} className="deidentification-grid">
      <DeidentificationCandidateList
        candidates={config.deIdentification.candidates}
        humanOnly={humanOnly}
        onCandidateReplacementChange={setCandidateReplacement}
        onCandidateToggle={setCandidateEnabled}
        onHumanOnlyChange={setHumanOnly}
        readonly={readonlyMode}
      />
      <div className="panel-stack">
        <header className="panel-card">
          <div className="workspace-title-group">
            <h2>{t('settings.deidentification.title')}</h2>
            <p className="workspace-subtitle">{t('settings.deidentification.description')}</p>
          </div>
          <label className="workspace-checkbox-row">
            <input checked={config.deIdentification.enabled} disabled={readonlyMode} onChange={(event) => setEnabled(event.currentTarget.checked)} type="checkbox" />
            {t('settings.deidentification.enabled')}
          </label>
          <p className="notice-text">{t('settings.deidentification.promptContract')}</p>
          <div className="form-grid form-grid--2">
            <label className="field-stack">
              <span className="field-label">{t('workspace.aiAssist.systemPrompt')}</span>
              <textarea className="workspace-textarea" aria-label={t('workspace.aiAssist.systemPrompt')} readOnly rows={5} value={config.promptContract.systemPromptTemplate} />
            </label>
            <label className="field-stack">
              <span className="field-label">{t('workspace.aiAssist.exampleResponse')}</span>
              <textarea className="workspace-textarea" aria-label={t('workspace.aiAssist.exampleResponse')} readOnly rows={8} value={JSON.stringify(config.promptContract.exampleResponse, null, 2)} />
            </label>
          </div>
          <p className="notice-text">{t('settings.deidentification.createNodePromptContract')}</p>
          <div className="form-grid form-grid--2">
            <label className="field-stack">
              <span className="field-label">{t('settings.deidentification.createNodeSystemPrompt')}</span>
              <textarea
                aria-label={t('settings.deidentification.createNodeSystemPrompt')}
                className="workspace-textarea"
                disabled={readonlyMode}
                onChange={(event) => setCreateNodePromptTemplate(event.currentTarget.value)}
                rows={5}
                value={config.createNodePromptContract.systemPromptTemplate}
              />
            </label>
            <label className="field-stack">
              <span className="field-label">{t('settings.deidentification.createNodeExampleJson')}</span>
              <textarea
                aria-label={t('settings.deidentification.createNodeExampleJson')}
                className="workspace-textarea"
                disabled={readonlyMode}
                onBlur={commitCreateNodeExampleJson}
                onChange={(event) => setCreateNodeExampleJson(event.currentTarget.value)}
                rows={8}
                value={createNodeExampleJson}
              />
            </label>
          </div>
          {createNodeExampleIssue && <p className="alert-text" role="alert">{createNodeExampleIssue}</p>}
          {readonlyMode && <p className="notice-text" role="note">{t('settings.deidentification.readonly')}</p>}
          {warnings.length > 0 && (
            <ul className="status-list" role="status">
              {warnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          )}
          {assembly.blockedRules.length > 0 && <p className="alert-text" role="alert">{t('settings.deidentification.conflictWarning')}</p>}
        </header>
        <ManualRuleEditor
          onAddRule={addManualRule}
          onRemoveRule={removeManualRule}
          onUpdateRule={updateManualRule}
          readonly={readonlyMode}
          rules={config.deIdentification.manualRules}
        />
        <PreviewTextPanel onInputChange={setPreviewInput} onRunPreview={runPreview} preview={previewSample} readonly={readonlyMode} />
      </div>
    </section>
  );
}
